import { storage } from './storage.js';

/**
 * Theme presets + application — web port of the v3 launcher's theme system.
 *
 * The six curated presets and the {bg, surface, accent, labelColor,
 * sectionColor} field shape are FROZEN (stability contract — same shape the
 * Android launcher persists under "launcher_theme"). The active theme is
 * additionally published to the registry under module id "launcher", key
 * "theme", so modules that call Shell.peek('launcher', 'theme') inherit the
 * colours exactly like on Android.
 */

export interface ThemePreset {
  id: string;
  name: string;
  bg: string;
  surface: string;
  accent: string;
  labelColor: string;
  sectionColor: string;
}

export const THEME_PRESETS: ThemePreset[] = [
  { id: 'default',  name: 'Lime',     bg: '#0e0f11', surface: '#16181c', accent: '#c8f060', labelColor: '#e8eaf0', sectionColor: '#6b7280' },
  { id: 'midnight', name: 'Midnight', bg: '#000000', surface: '#0e0e10', accent: '#22d3ee', labelColor: '#e8eaf0', sectionColor: '#5b6470' },
  { id: 'slate',    name: 'Slate',    bg: '#1a1d24', surface: '#242833', accent: '#60a5fa', labelColor: '#f0f2f7', sectionColor: '#7d8593' },
  { id: 'paper',    name: 'Paper',    bg: '#f7f7f5', surface: '#ffffff', accent: '#1c1c1c', labelColor: '#1c1c1c', sectionColor: '#8a8a85' },
  { id: 'warm',     name: 'Warm',     bg: '#faf6f0', surface: '#ffffff', accent: '#c2410c', labelColor: '#2a2a2a', sectionColor: '#a8957d' },
  { id: 'contrast', name: 'Contrast', bg: '#000000', surface: '#1a1a1a', accent: '#ffffff', labelColor: '#ffffff', sectionColor: '#cccccc' },
];

const THEME_KEY = 'shell.theme';
const THEME_MODULES_KEY = 'shell.themeModules';

/**
 * Whether the app theme's design tokens (and a forced dark color-scheme) are
 * injected into opened modules. Default on, but the user can turn it off when a
 * module renders its own colours — e.g. an embedded map whose tiles get washed
 * out by the forced dark scheme. Persisted; read by moduleHost on mount.
 */
export function isModuleThemingEnabled(): boolean {
  return localStorage.getItem(THEME_MODULES_KEY) !== '0';
}
export function setModuleThemingEnabled(on: boolean): void {
  localStorage.setItem(THEME_MODULES_KEY, on ? '1' : '0');
}

export function loadTheme(): ThemePreset {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<ThemePreset>;
      const preset = THEME_PRESETS.find((p) => p.id === saved.id);
      if (preset) return preset;
      // Unknown id (e.g. synced from a future version) — keep its colours.
      if (saved.bg && saved.surface && saved.accent) {
        return { ...THEME_PRESETS[0], ...saved } as ThemePreset;
      }
    }
  } catch { /* fall through to default */ }
  return THEME_PRESETS[0];
}

export function saveTheme(t: ThemePreset): void {
  localStorage.setItem(THEME_KEY, JSON.stringify(t));
  // Publish under module id "launcher" (not the host's '__launcher__') so
  // Shell.peek('launcher', 'theme') works for modules, mirroring Android.
  try {
    storage.set(
      { moduleId: 'launcher', key: '__published__::theme' },
      JSON.stringify({ ...t, bgImage: null }),
    );
  } catch { /* registry write is best-effort */ }
}

/**
 * The design-token catalog injected into every module. Generated modules
 * style themselves with var(--…) so the app theme reaches them; modules
 * that don't use the tokens are unaffected.
 *
 * `!important` is deliberate: the v3 built-in modules hard-code the same
 * token names in their own :root — the app's live theme must win over a
 * module's baked-in defaults.
 */
export function buildTokenCss(t: ThemePreset): string {
  const vars: Record<string, string> = {
    '--bg': t.bg,
    '--surface': t.surface,
    '--surface-2': shade(t.surface, 0.06),
    '--border': shade(t.surface, 0.12, true),
    '--accent': t.accent,
    '--accent-text': isLight(t.accent) ? '#0e0f11' : '#ffffff',
    '--label': t.labelColor,
    '--section': t.sectionColor,
    '--dim': t.sectionColor,
    // Semantic colors + radii — static across presets, part of the contract.
    '--danger': '#f87171',
    '--warn': '#f59e0b',
    '--ok': '#4ade80',
    '--info': '#60a5fa',
    '--r-card': '16px',
    '--r-tile': '22px',
    '--r-pill': '999px',
  };
  const body = Object.entries(vars).map(([k, v]) => `${k}: ${v} !important;`).join(' ');
  return `:root { ${body} }`;
}

/** Sets the CSS variables on :root. Derivation logic mirrors the v3 launcher. */
export function applyTheme(t: ThemePreset): void {
  const r = document.documentElement.style;
  r.setProperty('--bg', t.bg);
  r.setProperty('--surface', t.surface);
  r.setProperty('--surface-2', shade(t.surface, 0.06));
  r.setProperty('--accent', t.accent);
  r.setProperty('--accent-text', isLight(t.accent) ? '#0e0f11' : '#ffffff');
  r.setProperty('--label', t.labelColor);
  r.setProperty('--section', t.sectionColor);
  r.setProperty('--dim', t.sectionColor);
  r.setProperty('--border', shade(t.surface, 0.12, true));
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t.bg);
}

function isLight(hex: string): boolean {
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
}

/**
 * Lightens (dark surfaces) or darkens (light surfaces) a hex colour by
 * `amount` (0..1). With asAlphaBorder, returns an rgba() border colour
 * instead. Same cheap good-enough math as the v3 launcher.
 */
function shade(hex: string, amount: number, asAlphaBorder = false): string {
  const c = hex.replace('#', '');
  if (c.length < 6) return hex;
  let r = parseInt(c.slice(0, 2), 16);
  let g = parseInt(c.slice(2, 4), 16);
  let b = parseInt(c.slice(4, 6), 16);
  const light = (r + g + b) / 3 > 128;
  if (asAlphaBorder) return `rgba(${light ? '0,0,0' : '255,255,255'}, ${amount})`;
  const delta = Math.round(255 * amount) * (light ? -1 : 1);
  r = Math.max(0, Math.min(255, r + delta));
  g = Math.max(0, Math.min(255, g + delta));
  b = Math.max(0, Math.min(255, b + delta));
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}
